/* Balance Smarter (GuidedU) — tabs (desktop) + accordion (mobile)
 * Vanilla JS, no dependencies. One instance per [data-balance-smarter-guidedu].
 * Activated on DOMContentLoaded; safe to load with `defer`.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 899px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function BalanceSmarter(root) {
    this.root = root;
    this.tablistWrap = root.querySelector('.bsg-tablist-wrap');
    this.tablist = root.querySelector('.bsg-tablist');
    this.indicator = root.querySelector('.bsg-tab-indicator');
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-bsg-tab]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-bsg-panel]'));
    this.screens = Array.prototype.slice.call(root.querySelectorAll('[data-bsg-screen]'));

    if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

    this.activeIndex = Math.max(0, this.tabs.findIndex(function (t) {
      return t.getAttribute('aria-selected') === 'true';
    }));
    if (this.activeIndex === -1) this.activeIndex = 0;

    this.mobileMQ = window.matchMedia(MOBILE_QUERY);
    this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;

    this.buildAccordionShells();
    this.bindEvents();
    this.applyMode();
    this.observeReveal();

    root.setAttribute('data-bsg-ready', '');
  }

  BalanceSmarter.prototype.buildAccordionShells = function () {
    var self = this;
    this.shells = this.panels.map(function (panel, i) {
      var tab = self.tabs[i];

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'bsg-acc-trigger';
      trigger.id = 'bsg-acc-trigger-' + i + '-' + (panel.id || Math.random().toString(36).slice(2, 7));
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', i === self.activeIndex ? 'true' : 'false');

      var iconHTML = tab.querySelector('.bsg-tab-icon').innerHTML;
      var label = tab.querySelector('.bsg-tab-label').textContent;

      trigger.innerHTML =
        '<span class="bsg-acc-icon" aria-hidden="true">' + iconHTML + '</span>' +
        '<span class="bsg-acc-label">' + escapeHTML(label) + '</span>' +
        '<svg class="bsg-acc-chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
        '<path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      var existingChildren = Array.prototype.slice.call(panel.childNodes);
      var body = document.createElement('div');
      body.className = 'bsg-acc-body';
      var bodyInner = document.createElement('div');
      bodyInner.className = 'bsg-acc-body-inner';
      var bodyInnerPad = document.createElement('div');
      bodyInnerPad.className = 'bsg-acc-body-inner-pad';
      existingChildren.forEach(function (n) { bodyInnerPad.appendChild(n); });
      bodyInner.appendChild(bodyInnerPad);
      body.appendChild(bodyInner);

      panel.appendChild(trigger);
      panel.appendChild(body);

      trigger.addEventListener('click', function () {
        self.activate(i, { focus: false });
      });

      return { trigger: trigger, body: body };
    });
  };

  BalanceSmarter.prototype.bindEvents = function () {
    var self = this;

    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        self.activate(i, { focus: true });
      });
      tab.addEventListener('keydown', function (e) {
        self.handleKeydown(e, i);
      });
    });

    if (typeof ResizeObserver !== 'undefined' && this.tablist) {
      this.ro = new ResizeObserver(function () { self.updateIndicator(); });
      this.ro.observe(this.tablist);
    } else {
      window.addEventListener('resize', function () { self.updateIndicator(); });
    }

    var mqHandler = function () { self.applyMode(); };
    if (this.mobileMQ.addEventListener) this.mobileMQ.addEventListener('change', mqHandler);
    else this.mobileMQ.addListener(mqHandler);
  };

  BalanceSmarter.prototype.handleKeydown = function (e, i) {
    var key = e.key;
    var last = this.tabs.length - 1;
    var next = -1;

    if (key === 'ArrowRight') next = i === last ? 0 : i + 1;
    else if (key === 'ArrowLeft') next = i === 0 ? last : i - 1;
    else if (key === 'Home') next = 0;
    else if (key === 'End') next = last;
    else if (key === 'Enter' || key === ' ') {
      e.preventDefault();
      this.activate(i, { focus: true });
      return;
    } else return;

    e.preventDefault();
    this.activate(next, { focus: true });
  };

  BalanceSmarter.prototype.activate = function (index, opts) {
    if (index === this.activeIndex) return;
    opts = opts || {};
    this.activeIndex = index;

    this.tabs.forEach(function (tab, i) {
      var active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.shells.forEach(function (shell, i) {
      shell.trigger.setAttribute('aria-expanded', i === index ? 'true' : 'false');
    });

    this.screens.forEach(function (screen, i) {
      screen.classList.toggle('is-active', i === index);
    });

    this.applyPanelVisibility();
    this.updateIndicator();

    if (opts.focus && !this.mobileMQ.matches) {
      this.tabs[index].focus();
    }
  };

  BalanceSmarter.prototype.applyPanelVisibility = function () {
    var isMobile = this.mobileMQ.matches;
    var active = this.activeIndex;
    this.panels.forEach(function (panel, i) {
      if (isMobile || i === active) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  };

  BalanceSmarter.prototype.updateIndicator = function () {
    if (!this.indicator || !this.tablist) return;
    var tab = this.tabs[this.activeIndex];
    if (!tab) return;
    var x = tab.offsetLeft;
    var w = tab.offsetWidth;
    this.tablist.style.setProperty('--bsg-tab-x', x + 'px');
    this.tablist.style.setProperty('--bsg-tab-w', w + 'px');
    this.tablist.classList.add('is-ready');
  };

  BalanceSmarter.prototype.applyMode = function () {
    var index = this.activeIndex;
    this.shells.forEach(function (shell, i) {
      shell.trigger.setAttribute('aria-expanded', i === index ? 'true' : 'false');
    });
    this.applyPanelVisibility();
    if (!this.mobileMQ.matches) {
      requestAnimationFrame(this.updateIndicator.bind(this));
    }
  };

  BalanceSmarter.prototype.observeReveal = function () {
    var self = this;
    if (typeof IntersectionObserver === 'undefined') {
      this.root.classList.add('is-visible');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          self.root.classList.add('is-visible');
          io.disconnect();
        }
      });
    }, { threshold: 0.2 });
    io.observe(this.root);
  };

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function init() {
    document.querySelectorAll('[data-balance-smarter-guidedu]').forEach(function (root) {
      if (root.dataset.bsgInit) return;
      root.dataset.bsgInit = '1';
      new BalanceSmarter(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
